// The scenarios of a stage (R-U32, «цифровая воронка» of amoCRM): under a stage — its automations and «+ действие». A stage scenario is
// an ordinary automation of the core with the trigger «перешла на этап» (R-C6): no engine of its own. «Применить к уже стоящим» runs it
// once for the records on the stage now, through the engine with its attempts and log.

import { useState } from 'react';
import { uuidv7, type AutomationDef, type FieldDef, type Id, type Step, type TermDef } from '@confdeck/core-shared';
import { describeStep } from '../automations/describe.ts';
import { useMailTemplates } from '../automations/mail-templates.ts';
import { TermPicker, UserPicker } from '../fields/pickers.tsx';
import { newRecordData } from '../record/new-record.ts';
import { navigate } from '../shell/router.ts';
import { useMeta, useSync } from '../sync/react.tsx';
import { Switch } from '../ui/controls.tsx';
import { Button, Checkbox, Dialog, Field, Input, Menu, MenuContent, MenuItem, MenuTrigger, useToast } from '../ui/primitives.tsx';
import type { PipelineSectionDef } from './pipeline-section.ts';
import s from './pipelines.module.css';

type Kind = 'followUp' | 'mail' | 'owner' | 'webhook' | 'stage';
const KINDS: { kind: Kind; label: string; icon: string }[] = [
  { kind: 'followUp', label: 'Запись', icon: 'check' },
  { kind: 'mail', label: 'Письмо по шаблону', icon: 'mail' },
  { kind: 'owner', label: 'Сменить ответственного', icon: 'user' },
  { kind: 'webhook', label: 'Вебхук', icon: 'external' },
  { kind: 'stage', label: 'Сменить этап', icon: 'stage' },
];

interface Applied { onStage: number; queued: number; already: number; more: boolean }
/** «К стоящим на этапе»: the command over HTTP, for its counts — the socket answers only «done». */
async function applyToStage(id: Id, stageId: Id): Promise<{ ok: true; v: Applied } | { ok: false; message: string }> {
  const res = await fetch('/api/commands/automation.applyToStage', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ id, stageId }) });
  const body = await res.json().catch(() => ({})) as { result?: Applied; error?: { message: string } };
  return res.ok && body.result ? { ok: true, v: body.result } : { ok: false, message: body.error?.message ?? 'Не получилось запустить' };
}
const told = (v: Applied) => `запущено для ${v.queued} из ${v.onStage}${v.already ? `, уже было — ${v.already}` : ''}${v.more ? '. На этапе больше записей — нажмите ещё раз' : ''}`;

/** The scenarios whose trigger is «the stage field becomes this stage» (among others: «Выиграна» of any pipeline) — the stage's own ones. */
export function stageScenarios(automations: AutomationDef[], typeId: Id, field: string, stageId: Id): AutomationDef[] {
  return automations.filter((a) => !a.archivedAt && a.trigger.type === 'record.updated' && a.trigger.typeId === typeId && a.trigger.field === field
    && (a.trigger.to ?? []).map(String).includes(stageId));
}

export function StageScenarios({ stage, typeId, field, def }: { stage: TermDef; typeId: Id; field: FieldDef; def: PipelineSectionDef | undefined }) {
  const meta = useMeta();
  const client = useSync();
  const toast = useToast();
  const templates = useMailTemplates();
  const [adding, setAdding] = useState<Kind | null>(null);
  const list = stageScenarios(meta.automations(), typeId, field.key, stage.id);
  const run = async (command: string, input: unknown) => { const r = await client.run(command, input); if (!r.ok) toast({ kind: 'error', text: r.error.message }); return r; };
  const apply = async (a: AutomationDef) => {
    const r = await applyToStage(a.id, stage.id);
    toast(r.ok ? { kind: 'info', text: `«${a.name}»: ${told(r.v)}. Итог каждой — в журнале сценария.` } : { kind: 'error', text: r.message });
  };
  // The follow-up is named by the app («Задача»); without one the action is not offered.
  const kinds = KINDS.filter((k) => k.kind !== 'followUp' || !!def?.followUp).map((k) => (k.kind === 'followUp' ? { ...k, label: def!.followUp!.label } : k));
  return (
    <div className={s.scenarios} aria-label={`Сценарии этапа «${stage.name}»`}>
      {list.map((a) => (
        <div key={a.id} className={s.scenario}>
          <Switch size="sm" checked={a.enabled} label={a.enabled ? `Выключить «${a.name}»` : `Включить «${a.name}»`} onChange={(on) => void run('automation.toggle', { id: a.id, enabled: on })} />
          <button type="button" className={s.scenarioName} onClick={() => navigate(`/a/${a.id}`)} title="Открыть сценарий">{a.steps.map((st) => describeStep(st, meta, typeId, templates)).join(' → ') || a.name}</button>
          {a.enabled && <Button size="sm" variant="ghost" onClick={() => void apply(a)} title="Разовый запуск для записей, которые уже стоят на этапе; повтор не дублирует">К стоящим на этапе</Button>}
        </div>
      ))}
      <Menu>
        <MenuTrigger asChild><Button size="sm" variant="ghost" icon="plus" className={s.addAction}>действие</Button></MenuTrigger>
        <MenuContent>{kinds.map((k) => <MenuItem key={k.kind} icon={k.icon} onSelect={() => setAdding(k.kind)}>{k.label}</MenuItem>)}</MenuContent>
      </Menu>
      {adding && <StageAction kind={adding} stage={stage} typeId={typeId} field={field} def={def} onClose={() => setAdding(null)} />}
    </div>
  );
}

/** One action of a stage, set up in one dialog: what to do, its one setting, and whether to run it for those already on the stage. */
function StageAction({ kind, stage, typeId, field, def, onClose }: { kind: Kind; stage: TermDef; typeId: Id; field: FieldDef; def: PipelineSectionDef | undefined; onClose: () => void }) {
  const meta = useMeta();
  const client = useSync();
  const toast = useToast();
  const templates = useMailTemplates();
  const [text, setText] = useState(kind === 'followUp' ? 'Связаться: {{record.data.name}}' : kind === 'webhook' ? 'https://' : '');
  const [days, setDays] = useState(1);
  const [value, setValue] = useState<string | null>(null);
  const [toField, setToField] = useState<string | null>(meta.fields(typeId).find((f) => f.fieldType === 'email' && !f.archivedAt)?.key ?? null);
  const [existing, setExisting] = useState(false);
  const [busy, setBusy] = useState(false);
  const label = kind === 'followUp' ? def?.followUp?.label ?? 'Запись' : KINDS.find((k) => k.kind === kind)!.label;
  const emails = meta.fields(typeId).filter((f) => f.fieldType === 'email' && !f.archivedAt);
  const id = uuidv7().slice(-12);

  const step = (): Step | string => {
    switch (kind) {
      case 'followUp': {
        const f = def?.followUp;
        const type = f ? meta.typeByKey(f.typeKey) : undefined;
        if (!f || !type) return 'Раздел для новой записи не найден';
        if (!text.trim()) return 'Напишите, что сделать';
        const data = { ...newRecordData(meta, type.id, text.trim()), [f.person]: '{{record.ownerId}}', [f.due]: `{{today+${Math.max(0, days)}}}` };
        return { id, type: 'createRecord', typeId: type.id, data, ownerId: '{{record.ownerId}}', linkTo: f.link };
      }
      case 'mail':
        if (!value) return 'Выберите шаблон письма';
        if (!toField && !text.includes('@')) return 'Укажите адрес получателя';
        return { id, type: 'sendMail', template: value, ...(toField ? { toField } : { to: text.trim() }) };
      case 'owner':
        if (!value) return 'Выберите, кому передать';
        // The owner of the record is not a field of its data: it changes by the record's own command.
        return def?.mine && def.mine !== 'ownerId' ? { id, type: 'setField', field: def.mine, value } : { id, type: 'command', command: 'record.update', input: { id: '{{record.id}}', ownerId: value } };
      case 'webhook': return { id, type: 'webhook', url: text.trim() };
      case 'stage':
        if (!value || value === stage.id) return 'Выберите другой этап';
        return { id, type: 'setField', field: field.key, value };
    }
  };

  const save = async () => {
    const st = step();
    if (typeof st === 'string') { toast({ kind: 'error', text: st }); return; }
    setBusy(true);
    const aid = uuidv7();
    const saved = await client.run('automation.save', {
      id: aid, name: `Этап «${stage.name}»: ${label.toLowerCase()}`, description: `Сценарий этапа «${stage.name}»: срабатывает, когда запись переходит на этап.`,
      trigger: { type: 'record.updated', typeId, field: field.key, to: [stage.id] }, conditions: null, steps: [st],
    });
    // A stage scenario works at once, as a trigger of amoCRM does; it can be switched off in the row.
    const on = saved.ok ? await client.run('automation.toggle', { id: aid, enabled: true }) : saved;
    setBusy(false);
    if (!saved.ok) { toast({ kind: 'error', text: saved.error.message }); return; }
    if (!on.ok) { toast({ kind: 'error', text: on.error.message }); onClose(); return; }
    const applied = existing ? await applyToStage(aid, stage.id) : null;
    toast(applied && !applied.ok ? { kind: 'error', text: `Сценарий добавлен, но к стоящим не применён: ${applied.message}` }
      : { kind: 'info', text: applied?.ok ? `Сценарий этапа добавлен: ${told(applied.v)}` : 'Сценарий этапа добавлен' });
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()} label={`${label} на этапе «${stage.name}»`}>
      <div className={s.actionDialog}>
        <h2 className={s.actionTitle}>{label} — когда запись переходит на «{stage.name}»</h2>
        {kind === 'followUp' && <>
          <Field label="Что сделать"><Input value={text} onChange={(e) => setText(e.target.value)} /></Field>
          <Field label="Срок, дней от перехода"><Input type="number" min={0} value={days} onChange={(e) => setDays(Number(e.target.value))} /></Field>
          <p className={s.hint}>{label} — ответственному за запись и связана с ней. «{'{{record.data.name}}'}» — название записи.</p>
        </>}
        {kind === 'mail' && <>
          <Field label="Шаблон">
            <Menu>
              <MenuTrigger asChild><Button variant="secondary" caret>{value ? templates.get(value) ?? value : 'Выберите шаблон'}</Button></MenuTrigger>
              <MenuContent>{[...templates.entries()].map(([code, name]) => <MenuItem key={code} onSelect={() => setValue(code)}>{name}</MenuItem>)}</MenuContent>
            </Menu>
          </Field>
          {!templates.size && <p className={s.hint}>Шаблонов пока нет: создайте письмо в разделе «Письма».</p>}
          <Field label="Кому">
            <Menu>
              <MenuTrigger asChild><Button variant="secondary" caret>{toField ? emails.find((f) => f.key === toField)?.name ?? toField : 'Другой адрес'}</Button></MenuTrigger>
              <MenuContent>
                {emails.map((f) => <MenuItem key={f.key} onSelect={() => setToField(f.key)}>{f.name}</MenuItem>)}
                <MenuItem onSelect={() => setToField(null)}>Другой адрес</MenuItem>
              </MenuContent>
            </Menu>
          </Field>
          {!toField && <Field label="Адрес"><Input value={text} placeholder="почта@example.test" onChange={(e) => setText(e.target.value)} /></Field>}
        </>}
        {kind === 'owner' && <Field label="Передать"><UserPicker value={value} onChange={setValue} /></Field>}
        {kind === 'webhook' && <>
          <Field label="Адрес"><Input value={text} onChange={(e) => setText(e.target.value)} /></Field>
          <p className={s.hint}>Запись уходит POST-запросом в JSON. Не ответил 2xx — ещё две попытки; итог — в журнале запусков.</p>
        </>}
        {kind === 'stage' && <Field label="На этап"><TermPicker field={field} value={value} pipelineId={stage.parentId ?? undefined} onChange={(v) => setValue(typeof v === 'string' ? v : null)} /></Field>}
        <Checkbox checked={existing} onChange={setExisting}>Применить к уже стоящим на этапе — один раз</Checkbox>
        <footer className={s.actionFoot}>
          <Button onClick={onClose} disabled={busy}>Отмена</Button>
          <Button variant="primary" disabled={busy} onClick={() => void save()}>Добавить</Button>
        </footer>
      </div>
    </Dialog>
  );
}
